import styled, { css } from 'styled-components';
import { focusRing } from '@/app/components/search/styles/mixins';
import { IconCheckSolid } from '@/app/common/icons/libraryIcons';
import { Button, Text } from '@lmn/design-system';
import { DictionaryProvider } from '@lmn/lmn-components';
import { EvoFootprintBadge } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useIsMobile } from '@/app/components/search/utils/useSsrMedia';
import { adaptTransport } from '@/app/components/hotel-detail/flight-train/TransportDetails';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import EvoTransport from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/EvoTransport';
import { TransportDetailsContainer } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/styled';
import { Price, PriceWithTextContainer, TextPP } from '@/app/components/hotel-detail/changeTransport/Price';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { Icon } from '@/app/common/icons/Icon';
import type { ChangeTransportCardProps } from '@/app/components/hotel-detail/changeTransport/ChangeTransportCard';
import { EvoOvernightBanner } from '@/app/components/hotel-detail/evo/transport/EvoOvernightBanner';
import { toUniqueCompanies } from '@/app/components/hotel-detail/evo/transport/EvoTransportDetailsCard/utils';
import {
  CardContainer,
  CardContent,
  CardWrapper,
  TransportDivider,
  TransportCardSection,
  PriceAndButtonWrapper,
  PriceArea,
  SELECTED_LABEL_OVERLAP_PX,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportCardStyled';

// #region styled components
const SelectedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px ${SELECTED_LABEL_OVERLAP_PX + 8}px;
  background-color: ${(p) => p.theme.kallisto.color.background.interactive.backgroundPrimarySelected};
  border-radius: 16px 16px 0 0;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderBrandSubtle};
  border-bottom: none;
`;

const CheckMark = styled(IconCheckSolid)`
  width: 16px;
  height: 16px;
  color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
  flex-shrink: 0;
`;

const VerticalSeparator = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    width: 1px;
    background-color: ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
    align-self: stretch;
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
`;

const DetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-left: auto;
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
  text-decoration: underline;
  font-size: inherit;

  &:focus-visible {
    ${focusRing}
  }
`;

const PpLabel = styled(TextPP)`
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
`;

const SelectButton = styled(Button)`
  min-height: 48px;
  /* MPH-398: Figma button text is 16px (size200); size175 rendered 14px */
  font-size: ${({ theme }) => theme.kallisto.font.size['200']};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};

  @media (min-width: 768px) {
    width: 100%;
  }
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${(p) => p.theme.kallisto.color.common.white['1000']};
  opacity: 0.8;
`;

const EvoBaggageRow = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const EvoBaggageItem = styled.div<{ $hasLuggage: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(p) =>
    p.$hasLuggage ? p.theme.kallisto.color.text.static.textPrimary : p.theme.kallisto.color.text.static.textSecondary};
`;

const EvoBaggageSeparator = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  margin: 0 4px;
  flex-shrink: 0;
`;

const BaggageAndCo2Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// #endregion

function EvoBaggage({
  handBaggageIncluded,
  holdBaggageIncluded,
  numberOfBaggagePiecesIncluded,
}: {
  handBaggageIncluded: boolean;
  holdBaggageIncluded: boolean;
  numberOfBaggagePiecesIncluded?: number;
}) {
  const { adults, children } = useUrlSearchParams();

  const numberOfLuggage = (adults ?? 0) + (children ?? 0);
  const showGroupLuggage = (numberOfBaggagePiecesIncluded ?? 0) > 0;

  const handBagCount = handBaggageIncluded ? numberOfLuggage : 0;
  const holdBagCount = holdBaggageIncluded
    ? numberOfLuggage
    : showGroupLuggage
      ? (numberOfBaggagePiecesIncluded ?? 0)
      : 0;

  return (
    <EvoBaggageRow>
      <EvoBaggageItem $hasLuggage={numberOfLuggage > 0}>
        <Text variant="BodySmall">{numberOfLuggage}</Text>
        <Icon
          category="Luggage"
          name={numberOfLuggage > 0 ? 'icon-solid-backpack' : 'icon-backpack'}
          size={20}
          color="iconPrimary"
          alt=""
        />
      </EvoBaggageItem>
      <EvoBaggageSeparator />
      <EvoBaggageItem $hasLuggage={handBagCount > 0}>
        <Text variant="BodySmall">{handBagCount}</Text>
        <Icon
          category="Luggage"
          name={handBagCount > 0 ? 'icon-solid-carry-on' : 'icon-slashed-carry-on'}
          size={20}
          color={handBagCount > 0 ? 'iconPrimary' : 'iconSecondary'}
          alt=""
        />
      </EvoBaggageItem>
      <EvoBaggageSeparator />
      <EvoBaggageItem $hasLuggage={holdBagCount > 0}>
        <Text variant="BodySmall">{holdBagCount}</Text>
        <Icon
          category="Luggage"
          name={holdBagCount > 0 ? 'icon-solid-suitcase' : 'icon-slashed-suitcase'}
          size={20}
          color={holdBagCount > 0 ? 'iconPrimary' : 'iconSecondary'}
          alt=""
        />
      </EvoBaggageItem>
    </EvoBaggageRow>
  );
}

export function EvoTransportCard({
  inbound,
  outbound,
  co2Footprint,
  transportId,
  price,
  isEnabled = true,
  isTrain,
  numberOfBaggagePiecesIncluded,
  onOpenDetailsClick,
  onSelectClick,
}: ChangeTransportCardProps) {
  const { tripCardDictionary, t } = useTranslation();
  const { localization, commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: selectedTransportData } = useSelectedTransport();
  const { showTotalPrice, familyTotalPrice } = usePriceDisplay();
  const isSelected = selectedTransportData?.transports[0]?.id === transportId;

  const transportData = adaptTransport({ transportId, inbound, outbound });
  const { outbound: adaptedOutbound, inbound: adaptedInbound } = transportData;

  const outSegments = adaptedOutbound.segments;
  const outLen = outSegments.length - 1;
  const inSegments = adaptedInbound.segments;
  const inLen = inSegments.length - 1;

  const outboundCompanies = toUniqueCompanies(outSegments);
  const inboundCompanies = toUniqueCompanies(inSegments);

  const differentDepartureAirportCodes = outSegments[0].departure.hub.id !== inSegments[inLen].arrival.hub.id;
  const differentArrivalAirportCodes = outSegments[outLen].arrival.hub.id !== inSegments[0].departure.hub.id;

  const keepKey = isTrain
    ? 'vacation.oss.change_transport.card.keep_cta.train'
    : 'vacation.oss.change_transport.card.keep_cta.flight';
  const selectLabel = isTrain
    ? t('vacation.oss.evo_flights.card.select_cta.train')
    : t('vacation.oss.evo_flights.card.select_cta.flight');
  const selectedLabel = isTrain
    ? t('vacation.oss.change_transport.card.selected.train')
    : t('vacation.oss.change_transport.card.selected.flight');

  return (
    <CardContainer>
      {isSelected && (
        <SelectedLabel>
          <CheckMark />
          <Text
            variant="BodySmall"
            css={css`
              font-weight: ${(p) => p.theme.kallisto.font.weight.semibold};
              color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
            `}
          >
            {selectedLabel}
          </Text>
        </SelectedLabel>
      )}
      <CardWrapper $isSelected={isSelected}>
        <CardContent>
          <TransportCardSection>
            <TransportDetailsContainer $isMobile={isMobile}>
              <EvoTransport
                isOutBound={true}
                dictionary={tripCardDictionary}
                departureDate={outSegments[0].departure.datetime}
                departureAirportCode={outSegments[0].departure.hub.id}
                departureAirportName={outSegments[0].departure.hub.name}
                arrivalDate={outSegments[outLen].arrival.datetime}
                arrivalAirportCode={outSegments[outLen].arrival.hub.id}
                arrivalAirportName={outSegments[outLen].arrival.hub.name}
                providerCode={outSegments[0].transport.companyCode}
                providerName={outSegments[0].transport.companyName}
                numberOfStops={outLen}
                durationInMinutes={adaptedOutbound.durationInMinutes}
                locale={localization.locale}
                differentDepartureAirportCodes={differentDepartureAirportCodes}
                differentArrivalAirportCodes={differentArrivalAirportCodes}
                companies={outboundCompanies}
              />
              <VerticalSeparator />
              <EvoTransport
                isOutBound={false}
                dictionary={tripCardDictionary}
                departureDate={inSegments[0].departure.datetime}
                departureAirportCode={inSegments[0].departure.hub.id}
                departureAirportName={inSegments[0].departure.hub.name}
                arrivalDate={inSegments[inLen].arrival.datetime}
                arrivalAirportCode={inSegments[inLen].arrival.hub.id}
                arrivalAirportName={inSegments[inLen].arrival.hub.name}
                providerCode={inSegments[0].transport.companyCode}
                providerName={inSegments[0].transport.companyName}
                numberOfStops={inLen}
                durationInMinutes={adaptedInbound.durationInMinutes}
                locale={localization.locale}
                differentDepartureAirportCodes={differentDepartureAirportCodes}
                differentArrivalAirportCodes={differentArrivalAirportCodes}
                companies={inboundCompanies}
              />
            </TransportDetailsContainer>
            <TransportDivider />
            <FooterRow>
              <BaggageAndCo2Wrapper>
                {/* MPH-996: the card carries a single baggage row for the whole trip, so a piece counts
                    only when both bounds include it — the outbound alone used to decide, so a return
                    without a cabin bag still showed one, contradicting the trip-overview summary and
                    the flight page's own luggage block, both already fixed in MPH-932. */}
                <EvoBaggage
                  handBaggageIncluded={outbound.handBaggageIncluded && inbound.handBaggageIncluded}
                  holdBaggageIncluded={outbound.includeBaggage && inbound.includeBaggage}
                  numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
                />
                {co2Footprint && co2Footprint.co2kg > 0 && (
                  <DictionaryProvider labels={tripCardDictionary}>
                    <EvoFootprintBadge footprint={co2Footprint} locale={localization.locale} />
                  </DictionaryProvider>
                )}
              </BaggageAndCo2Wrapper>
              <DetailsLink
                type="button"
                onClick={onOpenDetailsClick}
                tabIndex={0}
                aria-label={t(isTrain ? 'vacation.select.trainmodal.title' : 'vacation.select.flightmodal.title')}
              >
                {/* MPH-398: Figma details link is 16px (Body), not 14px (BodySmall) */}
                <Text variant="Body">
                  {isMobile
                    ? t(
                        isTrain ? 'vacation.select.trainmodal.title.mobile' : 'vacation.select.flightmodal.title.mobile'
                      )
                    : t(isTrain ? 'vacation.select.trainmodal.title' : 'vacation.select.flightmodal.title')}
                </Text>
              </DetailsLink>
            </FooterRow>
            <EvoOvernightBanner inbound={inbound} outbound={outbound} onMessageClicked={onOpenDetailsClick} />
          </TransportCardSection>
          <PriceArea>
            <PriceAndButtonWrapper>
              <PriceWithTextContainer css="flex: 1; white-space: nowrap; justify-content: flex-start;">
                <Price>{price}</Price>
                {/* MPH-398: Figma uses the short "pp" suffix here (matches the sort sheet) */}
                {/* MPH-1292: a family delta carries no unit */}
                {!familyTotalPrice && (
                  <PpLabel>{t(showTotalPrice ? 'vacation.oss.cards.total' : 'vacation.oss.cards.pp')}</PpLabel>
                )}
              </PriceWithTextContainer>
              <SelectButton
                variant="primary"
                {...(!isSelected && { appearance: 'outlined' })}
                onClick={onSelectClick ?? onOpenDetailsClick}
                data-testid="select-flight-button"
              >
                {isSelected ? t(keepKey) : selectLabel}
              </SelectButton>
            </PriceAndButtonWrapper>
          </PriceArea>
        </CardContent>
        {!isEnabled && <DisabledOverlay />}
      </CardWrapper>
    </CardContainer>
  );
}

export type { ChangeTransportCardProps as EvoTransportCardProps };
